import { useAtom } from 'jotai';
import { dateTypeAtom, selectUserDataAtom } from 'store';
import { useState, useEffect, useCallback } from 'react';
import { _T } from '@dana/components';
import Nav from 'components/nav';
import { getDayCoinReward, DayCoinRewardType, getNewConfig, getNewConfigByMonth } from 'api';
import { newFlowItem } from 'api/type';
import { Popup } from 'react-vant';
import LevelRule from './level-rule/level-rule';
import dayjs from 'dayjs';

export default function DiamondReward() {
  const [userInfo] = useAtom(selectUserDataAtom);
  const [dateType] = useAtom(dateTypeAtom);

  const [list, setList] = useState<DayCoinRewardType[]>([]);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(false);
  const [configData, setConfigData] = useState<newFlowItem[] | undefined>(undefined);

  // 获取当月的开始和结束日期（YYYYMMDD格式）
  const getMonthRange = useCallback(() => {
    const start = Number(dayjs().startOf('month').format('YYYYMMDD'));
    const end = Number(dayjs().endOf('month').format('YYYYMMDD'));
    return { start, end };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getMonthRange();
      const uid = userInfo?.uid;
      if (!uid) {
        console.error('No uid available');
        return;
      }
      const res = await getDayCoinReward(uid, start, end);
      if (res) {
        setList(res);
      }
    } catch (error) {
      console.error('Failed to fetch diamond reward data:', error);
    } finally {
      setLoading(false);
    }
  }, [getMonthRange, userInfo?.uid]);

  const fetchConfig = useCallback(async () => {
    try {
      const res =
        dateType === 'HALF_MONTH'
          ? await getNewConfig(dayjs().format('YYYYMMDD'))
          : await getNewConfigByMonth(0);
      if (res) {
        setConfigData(res);
      }
    } catch (error) {
      console.error('Failed to fetch config data:', error);
    }
  }, [dateType]);

  const handleQuestionClick = () => {
    if (!configData) {
      fetchConfig();
    }
    setPopup(true);
  };

  useEffect(() => {
    fetchData();
    fetchConfig();
  }, [fetchData, fetchConfig]);

  // 格式化日期显示
  const formatDate = (timestamp: number) => {
    return dayjs(timestamp).format('YYYY/MM/DD');
  };

  return (
    <div className="bg-[#F2F5F5] min-h-screen pb-[12px]">
      <Popup visible={popup} closeIconPosition="bottom">
        <div className="flex flex-col items-center">
          <LevelRule titleList={[_T('等级'), _T('收到的钻石'), _T('金币奖励')]} title={_T('钻石流水&奖励')} data={configData}></LevelRule>
          <div
            onClick={() => setPopup(false)}
            className="w-[36px] h-[36px] bg-cover mt-[12px]"
            style={{ backgroundImage: `url(${require('asset/imgs/close.png')})` }}
          ></div>
        </div>
      </Popup>
      <Nav title={_T('钻石流水&奖励')}></Nav>
      <div
        className="bg-[#F2F5F5] pt-[12px] overflow-scroll"
        style={{
          height: `${window.innerHeight - 45}px`,
        }}
      >
        <div className="mx-auto pt-[12px] w-[351px] rounded-[8px] bg-white pb-[12px] mt-[12px]">
          {/* 用户信息 */}
          <div className="flex justify-between items-center">
            <div className="flex justify-start">
              <div
                className="ml-[12px] w-[52px] h-[52px] rounded-full bg-cover bg-gray-200"
                style={{
                  backgroundImage: userInfo?.avatar ? `url(${userInfo.avatar})` : undefined,
                }}
              ></div>
              <div className="flex flex-col justify-start items-start ml-[12px]">
                <div
                  className="mt-[10px] text-[#333] font-sans text-[14px] font-bold leading-[18px] truncate w-[190px]"
                  dir="auto"
                >
                  {userInfo?.nickName || 'Passwordpa'}
                </div>
                <div className="text-[#B3B3B3] font-sans text-[14px] font-semibold leading-[18px]">
                  ID: {userInfo?.danaId || '123456'}
                </div>
              </div>
            </div>
            <div
              onClick={handleQuestionClick}
              className="w-[20px] h-[20px] mr-[12px] bg-cover cursor-pointer"
              style={{
                backgroundImage: `url(${require('asset/imgs/q.png')})`,
              }}
            ></div>
          </div>

          {/* 表头 */}
          <div className="ml-[12px] flex justify-between items-center w-[327px] h-[48px] rounded-[8px] bg-[#F5F7F7] mt-[12px] px-[8px]">
            <div className="flex-1 text-[#16B28D] text-center font-sans text-[14px] font-bold" dir="auto">
              {_T('日期')}
            </div>
            <div className="flex-1 text-[#16B28D] text-center font-sans text-[14px] font-bold" dir="auto">
              {_T('收到的钻石')}
            </div>
            <div className="flex-1 text-[#16B28D] text-center font-sans text-[14px] font-bold" dir="auto">
              {_T('金币奖励')}
            </div>
          </div>

          {/* 数据列表 */}
          <div className="rounded-[8px] bg-[#F5F7F7] flex flex-col justify-center items-center w-[327px] mx-auto mt-[12px] pb-[12px]">
            {loading ? (
              <div className="py-[20px] text-[#B3B3B3] text-center font-sans text-sm">
                {/* {_T('Loading...')} */}
              </div>
            ) : list.length === 0 ? (
              <div className="py-[20px] text-[#B3B3B3] text-center font-sans text-sm">
                {/* {_T('No data')} */}
              </div>
            ) : (
              list.map((item, index) => (
                <div
                  key={item.id || index}
                  className="mt-[12px] w-full flex justify-between items-center px-[8px]"
                >
                  <div
                    dir="auto"
                    className="flex-1 text-[#B3B3B3] text-center font-sans text-sm font-semibold"
                  >
                    {dayjs(String(item.guildDay)).format('YYYY-MM-DD')}
                  </div>
                  <div className="flex-1 flex justify-center items-center">
                    <span
                      dir="auto"
                      className="text-[#B3B3B3] text-center font-sans text-sm font-semibold mr-[4px]"
                    >
                      {item.giftFlowTotal.toLocaleString()}
                    </span>
                    {/* <div
                      className="w-[16px] h-[13px] bg-cover"
                      style={{
                        backgroundImage: `url(${require('asset/imgs/diamond.png')})`,
                      }}
                    ></div> */}
                  </div>
                  <div
                    dir="auto"
                    className="flex-1 text-[#B3B3B3] text-center font-sans text-sm font-semibold"
                  >
                    {item?.reward || '-'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
